/**
 * MenuScene - Main menu with play button and settings
 * Tactile Minimalism aesthetic with simple, clean UI.
 */

import Phaser from 'phaser';
import { SCENES, COLORS, GAME } from '../config/gameConfig';
import { AudioManager } from '../systems/AudioManager';
import { HapticsManager } from '../systems/HapticsManager';
import { SettingsPanel } from '../ui/SettingsPanel';

export class MenuScene extends Phaser.Scene {
  private audioManager!: AudioManager;
  private haptics!: HapticsManager;
  private settingsPanel!: SettingsPanel;

  constructor() {
    super({ key: SCENES.MENU });
  }

  preload(): void {
    // Initialize audio manager and preload audio assets
    this.audioManager = new AudioManager(this);
    this.audioManager.preload();
  }

  create(): void {
    this.cameras.main.setBackgroundColor(COLORS.BACKGROUND);

    // Initialize audio and haptics
    this.audioManager.create();
    this.haptics = new HapticsManager();

    const centerX = GAME.WIDTH / 2;
    const centerY = GAME.HEIGHT / 2;

    // Title
    const title = this.add.text(centerX, centerY - 150, 'ZENITH', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '64px',
      color: '#2D3436',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5, 0.5);

    // Subtitle
    const subtitle = this.add.text(centerX, centerY - 80, 'Build higher.', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '24px',
      color: '#636E72',
    });
    subtitle.setOrigin(0.5, 0.5);

    // Play button
    this.createPlayButton(centerX, centerY + 50);

    // Settings button
    this.createSettingsButton();

    // Create settings panel
    this.settingsPanel = new SettingsPanel(this, this.audioManager, this.haptics);
  }

  private createPlayButton(x: number, y: number): void {
    // Button background
    const buttonWidth = 200;
    const buttonHeight = 60;

    const button = this.add.graphics();
    button.fillStyle(COLORS.PLATFORM, 1);
    button.fillRoundedRect(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight, 12);

    // Button text
    const buttonText = this.add.text(x, y, 'PLAY', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '28px',
      color: '#FFFFFF',
      fontStyle: 'bold',
    });
    buttonText.setOrigin(0.5, 0.5);

    // Interactive zone
    const hitArea = this.add.rectangle(x, y, buttonWidth, buttonHeight, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });

    hitArea.on('pointerdown', () => {
      // Visual feedback
      button.clear();
      button.fillStyle(COLORS.UI_ACCENT, 1);
      button.fillRoundedRect(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight, 12);

      // Audio and haptic feedback
      this.audioManager.playButtonTap();
      this.haptics.buttonTap();
    });

    hitArea.on('pointerup', () => {
      this.cleanupScene();
      this.scene.start(SCENES.GAME);
    });

    hitArea.on('pointerout', () => {
      button.clear();
      button.fillStyle(COLORS.PLATFORM, 1);
      button.fillRoundedRect(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight, 12);
    });
  }

  private createSettingsButton(): void {
    const settingsBtn = this.add.text(GAME.WIDTH - 25, 40, '⚙', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '28px',
      color: '#636E72',
      stroke: '#ffffff',
      strokeThickness: 2,
    });
    settingsBtn.setOrigin(1, 0);
    settingsBtn.setInteractive({ useHandCursor: true });
    settingsBtn.setDepth(100);

    settingsBtn.on('pointerdown', () => {
      this.audioManager.playButtonTap();
      this.haptics.buttonTap();
      this.settingsPanel.show();
    });

    settingsBtn.on('pointerover', () => {
      settingsBtn.setColor('#2D3436');
      settingsBtn.setScale(1.1);
    });
    settingsBtn.on('pointerout', () => {
      settingsBtn.setColor('#636E72');
      settingsBtn.setScale(1);
    });
  }

  private cleanupScene(): void {
    this.audioManager.destroy();
    this.settingsPanel.destroy();
  }
}
